// src/pages/OrderSuccess.jsx
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Package, Home, Copy, Check } from "lucide-react";

function FontImport() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;1,9..144,500&family=DM+Sans:wght@400;500;600;700&display=swap');
      .og-display{font-family:'Fraunces',serif}
      .og-body{font-family:'DM Sans',sans-serif}
      @keyframes popIn {
        0% { transform: scale(0.6); opacity: 0; }
        60% { transform: scale(1.08); opacity: 1; }
        100% { transform: scale(1); }
      }
      @keyframes drawCheck {
        to { stroke-dashoffset: 0; }
      }
      .success-circle { animation: popIn 0.5s cubic-bezier(.34,1.56,.64,1) both; }
    `}</style>
  );
}

const YELLOW = "#FFD400";

export default function OrderSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const orderId = location.state?.orderId;
  const [copied, setCopied] = useState(false);

  // Agar koi seedha URL type karke aa jaye bina order ke, tab bhi page crash na ho
  useEffect(() => {
    if (!orderId) {
      // optional: kuch der baad home pe bhej sakte ho, abhi ke liye bas fallback UI dikhayenge
    }
  }, [orderId]);

  const handleCopy = () => {
    if (!orderId) return;
    navigator.clipboard.writeText(orderId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="og-body min-h-[70vh] bg-white text-[#1A1A1A] flex items-center justify-center px-4 py-14 sm:py-20">
      <FontImport />

      <div className="w-full max-w-lg text-center">
        {/* Success icon */}
        <div className="success-circle mx-auto mb-6 w-20 h-20 rounded-full flex items-center justify-center"
          style={{ background: "#EAF9EE" }}>
          <CheckCircle2 size={44} strokeWidth={2} className="text-green-600" />
        </div>

        <p className="text-xs font-semibold tracking-wider uppercase text-gray-400 mb-2">
          Order Confirmed
        </p>
        <h1 className="og-display italic text-3xl sm:text-4xl font-medium mb-3">
          Thank you for your order!
        </h1>
        <p className="text-sm sm:text-base text-gray-500 mb-8 leading-relaxed">
          We've received your order and we're getting it ready.
          You'll get a confirmation on your phone/email shortly with the delivery details.
        </p>

        {/* Order ID card */}
        {orderId && (
          <div className="rounded-xl border border-gray-200 bg-[#F9F9F9] px-5 py-4 mb-8 flex items-center justify-between gap-3">
            <div className="text-left">
              <p className="text-xs text-gray-400 mb-0.5">Order Number</p>
              <p className="font-semibold text-sm sm:text-base">{orderId}</p>
            </div>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 text-xs font-medium border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-50 flex-shrink-0"
            >
              {copied ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        )}

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10 text-left">
          {[
            { title: "Order Placed", desc: "We've got your order", done: true },
            { title: "Processing", desc: "Being packed with care", done: false },
            { title: "On the way", desc: "Shipped to your address", done: false },
          ].map((s, i) => (
            <div key={i} className="rounded-xl border border-gray-200 px-4 py-3.5">
              <div className={`w-2 h-2 rounded-full mb-2 ${s.done ? "bg-green-600" : "bg-gray-300"}`} />
              <p className="text-xs font-semibold">{s.title}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate("/")}
            className="flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-bold text-[#1A1A1A] hover:brightness-95 transition-all"
            style={{ background: YELLOW }}
          >
            <Home size={16} /> Continue Shopping
          </button>
          <Link
            to="/"
            className="flex items-center justify-center gap-2 rounded-xl border border-gray-300 px-6 py-3 text-sm font-semibold hover:bg-gray-50 transition-all"
          >
            <Package size={16} /> Track Order
          </Link>
        </div>

        <p className="text-xs text-gray-400 mt-8">
          Need help? <Link to="#" className="underline underline-offset-2 hover:text-[#1A1A1A]">Contact support</Link>
        </p>
      </div>
    </div>
  );
}